<template>
  <div
    class="settings-container"
  >
    <PlayAsTeamSetUp
      v-if="isShowTeamSetup"
      @showTeamSetUp="isShowTeamSetup = !isShowTeamSetup"
      @hideMainHeader="$emit('handle-hide-main-header')"
      @updatePresentation="_updatePresentationFromQuizTeamConfig"
    />

    <aha-accordion v-else class="settings-accordion">
      <aha-accordion-item
        :default-expanded="defaultExpandedAccordion=='information'"
      >
        <template
          slot="title"
        >
          <span class="w-full h-full" ref="presentInformation" @click="onClickPresenInfoTab">
            <h5
              class="flex items-center"
            >
              {{ $i18nPresenter.t('Presentation info') }}
              <aha-badge
                label="NEW"
                size="2xs"
                class="bg-primary-90 ml-2.5 cursor-pointer settings-accordion__badge"
              />
            </h5>
          </span>
        </template>
        <template slot="content">
          <setting-metadata
            :presentation="presentation"
            :currentCategories="currentCategories"
          ></setting-metadata>
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion=='audienceAdmission'">
        <template slot="title">
          <h5 class="flex items-center">
            {{ $i18nPresenter.t('Collect audience info') }}
            <div class="relative" v-if="isPlanFree && isBeforeCurrentPeriodStart">
              <img src="@/assets/images/pro.svg" class="ml-2 cursor-pointer" @click="openUpgradeBackground" />
              <PlanUpgradeModal
                :plan-for-feature="'Essential'"
                :name-feature="'Collect audience info'"
                :isShowTooltip="ctaUpgradeParticipantInfo"
                @outsideEvent="clickOutsideParticipantInfo"
              />
              <div v-if="ctaUpgradeParticipantInfo" class="upgrade-arrow upgrade-arrow_slide"></div>
            </div>
          </h5>
        </template>
        <template slot="content">
          <SettingAudienceAdmission @open-when-user-not-paid="openUpgradeBackground" />
          <div
            v-if="!isEURegion && isNotShowAttendanceCheckBefore"
            class="pt-4 border-b border-base-30"
          ></div>
          <AttendanceCheck
            v-if="!isEURegion && isNotShowAttendanceCheckBefore"
            :fromLabel="'Setting > Collect audience info'"
            @addParticipantsTracker="addParticipantsTracker('Setting > Collect audience info')"
          />
        </template>
      </aha-accordion-item>
      <aha-accordion-item :default-expanded="defaultExpandedAccordion=='audienceAuthentication'">
        <template slot="title">
          <h5 class="flex items-center">
            {{ $i18nPresenter.t('Audience authentication') }}
          </h5>
        </template>
        <template slot="content">
          <SettingAudienceAuthentication />
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion=='qna'">
        <template slot="title">
          <h5>{{ $i18nPresenter.t('Q&A with your audience') }}</h5>
        </template>
        <template slot="content">
          <SettingQna @updatePresentation="_updatePresentation" />
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion=='quiz'">
        <template slot="title">
          <h5 class="flex items-center">
            {{ $i18nPresenter.t('General quiz settings') }}
          </h5>
        </template>
        <template slot="content">
          <SettingQuiz
            @showTeamSetUp="isShowTeamSetup = !isShowTeamSetup"
            @hideMainHeader="$emit('handle-hide-main-header')"
            @updatePresentation="_updatePresentationFromQuizTeamConfig"
          />
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion=='lang'">
        <template slot="title">
          <h5>{{ $i18nPresenter.t('Language') }}</h5>
        </template>

        <template slot="content">
          <div class="relative">
            <p class="mb-4 text-xs">
              {{ $i18nPresenter.t(`Choose which language to display to your audience when they join the presentation from their smartphones.`) }}
            </p>
            <div class="relative mb-3">
              <select
                class="w-full p-3 pr-8 border rounded outline-none appearance-none cursor-pointer
                  focus:bg-primary-20 focus:border-primary-90 bg-base-0 focus:outline-none
                  border-secondary-20"
                v-model="$store.state.presentation.language"
                @change="setLang()"
              >
                <option value="id">
                  Bahasa Indonesia
                </option>
                <option value="da">
                  Dansk
                </option>
                <option value="de">
                  Deutsch
                </option>
                <option value="en">
                  English
                </option>
                <option value="es">
                  Español
                </option>
                <option value="fr">
                  Français
                </option>
                <option value="it">
                  Italiano
                </option>
                <option value="ja">
                  日本語
                </option>
                <option value="lv">
                  Latviešu Valoda
                </option>
                <option value="nl">
                  Nederlands
                </option>
                <option value="br">
                  Português
                </option>
                <option value="pl">
                  Polski
                </option>
                <option value="ru">
                  Pусский
                </option>
                <option value="si">
                  Slovenščina
                </option>
                <option value="tr">
                  Türkçe
                </option>
                <option value="vi">
                  Tiếng Việt
                </option>
                <option value="zh">
                  中文
                </option>
              </select>
              <aha-icon
                class="absolute ml-auto pointer-events-none top-4 right-3"
                name="arrow"
                viewBox="0 0 24 24"
                width="12"
                height="12"
              />
            </div>

            <aha-checkbox
              class="mb-3 w-max"
              id="hideJoining"
              v-model="presentation.filteringProfanity"
              @change="handleUpdateProfanityFilter()"
            >
              {{ $i18nPresenter.t('Filter profanity') }}
              <aha-icon
                class="mt-0.5 ml-1 text-base-70"
                name="question"
                viewBox="0 0 24 24"
                width="15"
                height="15"
                v-tooltip="$i18nPresenter.t('Hide inappropriate words submitted by the audience on all slides')"
              />
            </aha-checkbox>

            <p class="text-xs">
              {{ $i18nPresenter.t('Help us improve the translations or profanity filtering by emailing us at') }}
              <a
                class="text-primary-90"
                href="mailto:hi@ahaslides.com"
              >hi@ahaslides.com</a>.
            </p>

            <div class="overlay-disable-setting" v-if="isDisableEditPresentation"></div>
          </div>
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion=='pacing'">
        <template slot="title">
          <h5 class="flex items-center">
            {{ $i18nPresenter.t('Who takes the lead') }}
          </h5>
        </template>

        <template slot="content">
          <div class="relative">
            <AhaInfoBox class="mb-3">
              {{ $i18nPresenter.t(`What’s new: The audience (self-paced) option is now available for quiz questions.`) }}
            </AhaInfoBox>

            <aha-radio
              class="mb-3"
              name="whoTakesTheLead"
              :value="false"
              v-model="$store.state.presentation.audiencePacing"
              @change="onChangeAudiencePacing"
            >
              {{ $i18nPresenter.t('Presenter') }}
              <aha-icon
                class="ml-1 text-base-70"
                name="question"
                viewBox="0 0 24 24"
                width="15"
                height="15"
                v-tooltip="presenterPacingTooltip"
              />
            </aha-radio>

            <aha-radio
              name="whoTakesTheLead"
              :value="true"
              v-model="$store.state.presentation.audiencePacing"
              @change="onChangeAudiencePacing"
            >
              {{ $i18nPresenter.t('Audience (self-paced)') }}
              <aha-icon
                class="ml-1 text-base-70"
                name="question"
                viewBox="0 0 24 24"
                width="15"
                height="15"
                v-tooltip="$i18nPresenter.t('Participants start from the first slide and proceed on their own. Great for surveys')"
              />
            </aha-radio>
            <p class="mt-2 ml-5 text-xs">
              {{ $i18nPresenter.t('To invite your audience, please go to') }}
              <aha-link
                @click="showShareMenu"
                type="primary"
                variant="primary"
              >
                <span class="text-xs font-bold">{{ $i18nPresenter.t('Share') }}</span>
              </aha-link>.
            </p>

            <div class="overlay-disable-setting" v-if="isDisableEditPresentation"></div>
          </div>
        </template>
      </aha-accordion-item>
      <aha-accordion-item
        :default-expanded="defaultExpandedAccordion=='other'"
      >
        <template slot="title">
          <h5>{{ $i18nPresenter.t('Other settings') }}</h5>
        </template>
        <template slot="content">
          <div class="relative">
            <p>
              <aha-checkbox
                class="mb-3"
                id="hideJoining"
                v-model="presentation.hideInstructionBar"
                @change="_hideInstructionBar()"
              >
                {{ $i18nPresenter.t('Hide the joining instruction bar') }}
              </aha-checkbox>
            </p>
            <p>
              <aha-checkbox
                class="mb-3"
                id="makeLinksClickable"
                v-model="presentation.showHyperLink"
                @change="_linkClickAble()"
              >
                {{ $i18nPresenter.t('Make links clickable') }}
              </aha-checkbox>
            </p>

            <div class="flex justify-start mb-3">
              <aha-checkbox
                v-model="presentation.isEnablePrivateFeedback"
                @change="togglePrivateFeedback()"
              >
                {{ $i18nPresenter.t('Allow audience feedback') }}
              </aha-checkbox>
              <div class="relative">
                <div class="cursor-pointer">
                  <aha-icon
                    class="ml-1 text-base-70"
                    name="question"
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    v-tooltip="$i18nPresenter.t('Audience can send you private feedback.')"
                  />
                </div>
              </div>
            </div>

            <div class="flex justify-start mb-3">
              <aha-checkbox
                id="showCTA"
                :checked="true"
                v-model="$store.state.presentation.showAhaSlidesCTA"
                @change="setShowAhaSlidesCTA()"
                :disabled="!(isWhiteLabel || isProPlan || isMSAccount)"
              >
                {{ $i18nPresenter.t('Show sign-up link') }}
              </aha-checkbox>
              <div class="relative">
                <div v-if="(isWhiteLabel || isProPlan)" class="cursor-pointer">
                  <aha-icon
                    class="ml-1 text-base-70"
                    name="question"
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    v-tooltip="$i18nPresenter.t('Participants can request slides at the final slide.')"
                  />
                </div>

                <div
                  v-else
                  class="ml-2 cursor-pointer relative"
                  @click="openUpgrade()"
                >
                  <img src="@/assets/images/pro.svg" />
                  <PlanUpgradeModal
                    :name-feature="'Allow slide requests'"
                    :isShowTooltip="ctaUpgrade"
                    @outsideEvent="clickOutsideUp"
                  />
                  <div v-if="ctaUpgrade" class="upgrade-arrow"></div>
                </div>
              </div>
            </div>

            <!-- Reaction setting -->
            <div
              class="mb-2 font-bold"
            >
              {{ $i18nPresenter.t('Reactions') }}
              <AhaSvg
                name="question"
                class="cursor-pointer text-base-70"
                v-tooltip="$i18nPresenter.t('Let the audience send reactions during your presentation')"
              />
            </div>
            <div class="flex mb-3">
              <aha-checkbox
                class="settings-reaction__checkbox"
                @change="handleEnableSingleReaction(reaction.name, $event)"
                v-model="presentation.reactions[reaction.name]"
                v-for="(reaction, index) in reactionImages"
                :key="index"
              >
                <img
                  :src="reaction.icon"
                  class="settings-reaction__image"
                  v-if="presentation.reactions[reaction.name]"
                >
                <img :src="reaction.iconDefault" class="settings-reaction__image" v-else />
              </aha-checkbox>
            </div>

            <template
              v-if="currentUser.role === 'admin' || currentUser.email === 'template@ahaslides.com'"
            >
              <h5 class="mb-3 font-bold">
                {{ $i18nPresenter.t('Tags') }}
              </h5>
              <Tags class="mb-4" />
            </template>

            <div class="overlay-disable-setting" v-if="isDisableEditPresentation"></div>
          </div>
        </template>
      </aha-accordion-item>
    </aha-accordion>
  </div>
</template>

<script>

import { mapState, mapActions } from 'vuex'
import Util from '@/util'
import Config from '@/config'
import Constant from '@/constant'
import Socket from '@/socket'

// api
import PresentationApi from '@/api/presentation'
import eventCategories from '@/constant/eventCategories'

// components
import SettingAudienceAdmissionContainer from '@/containers/header-settings/SettingAudienceAdmissionContainer'
import Tags from '@/components-v2/presentation-editor/editor-header/Tags.vue'
import SettingAudienceAdmission from './SettingAudienceAdmission.vue'
import SettingAudienceAuthentication from './SettingAudienceAuthentication.vue'
import SettingQna from './SettingQnA.vue'
import SettingQuiz from './SettingQuiz.vue'
import SettingMetadata from './SettingMetadata.vue'
import PlayAsTeamSetUp from './PlayAsTeamSetUp.vue'
import PlanUpgradeModal from '@/components-v2/presentation-editor/editor-header/PlanUpgrade.vue'

// reactions
const like = require('@/assets/images/reactions/like.svg')
const likeDefault = require('@/assets/images/reactions/like-default.svg')
const heart = require('@/assets/images/reactions/heart.svg')
const heartDefault = require('@/assets/images/reactions/heart-default.svg')
const laugh = require('@/assets/images/reactions/laugh.svg')
const laughDefault = require('@/assets/images/reactions/laugh-default.svg')
const wow = require('@/assets/images/reactions/wow.svg')
const wowDefault = require('@/assets/images/reactions/wow-default.svg')
const sad = require('@/assets/images/reactions/sad.svg')
const sadDefault = require('@/assets/images/reactions/sad-default.svg')

const reactionImages = [
  {
    name: 'like',
    icon: like,
    iconDefault: likeDefault,
  },
  {
    name: 'heart',
    icon: heart,
    iconDefault: heartDefault,
  },
  {
    name: 'laugh',
    icon: laugh,
    iconDefault: laughDefault,
  },
  {
    name: 'wow',
    icon: wow,
    iconDefault: wowDefault,
  },
  {
    name: 'sad',
    icon: sad,
    iconDefault: sadDefault,
  },
]
export default {
  components: {
    PlanUpgradeModal,
    SettingAudienceAdmission: SettingAudienceAdmissionContainer(SettingAudienceAdmission),
    SettingQna,
    SettingQuiz,
    SettingMetadata,
    PlayAsTeamSetUp,
    Tags,
    SettingAudienceAuthentication,
  },
  props: {
    defaultExpandedAccordion: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    isShowTeamSetup: false,
    teamPlay: false,
    ctaUpgradeParticipantInfo: false,
    teamCount: Constant.defaultQuizTeam.teamCount,
    teamSize: Constant.defaultQuizTeam.teamSize,
    limit: 500,
    reactionImages,
    reactions: {},
    ctaUpgrade: false,
  }),
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,

    }),
    currentCategories: {
      get() {
        return this.presentation.PresentationsCategories
      },
    },
    presenterPacingTooltip() {
      return this.$i18nPresenter.t('The audience follow you. They can\'t proceed to the next slide on their own.')
    },
    isPlanFree() {
      return !this.$store.state.plan || this.$store.state.plan.group === 'free'
    },
    isBeforeCurrentPeriodStart() {
      return this.$store.state.currentUser.isBeforeCurrentPeriodStart
    },
    isWhiteLabel() {
      return this.$store.state.plan.group === 'white-label'
    },
    isProPlan() {
      return this.$store.state.plan.group == 'pro'
    },
    isMSAccount() {
      return this.currentUser && this.currentUser.email && this.currentUser.email.includes('onmicrosoft.com')
    },
    isEURegion() {
      return Config.region === 'eu'
    },
    isNotShowAttendanceCheckBefore() {
      return !window.localStorage.getItem('showAttendancePopupBefore')
    },

  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.presentation.teamSize': function () {
      this.teamSize = this.$store.state.presentation.teamSize
    },
    defaultExpandedAccordion() {
      if (this.defaultExpandedAccordion === 'information') {
        this.$refs.presentInformation.click()
      }
    },
  },
  created() {
    this.teamPlay = this.presentation.teamPlay
    this.teamCount = this.presentation.teamCount
    this.teamSize = this.presentation.teamSize
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'patchPresentationAction']),
    setShowAhaSlidesLogo() {
      const payload = {
        notShowAhaSlidesLogo: this.$store.state.presentation
          .notShowAhaSlidesLogo,
      }
      this.setSyncLoadingAction(1)
      this._updatePresentation(payload)
    },

    handleUpdateProfanityFilter() {
      const payload = {
        socketId: Socket.getSocketId(),
        filteringProfanity: this.presentation.filteringProfanity,
      }
      this.setSyncLoadingAction(1)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        `Header > Settings > Profanity Filter > ${this.presentation.filteringProfanity ? 'Filter on' : 'Filter off'}`,
      )
      this._updatePresentation(payload)
    },
    clickOutsideParticipantInfo() {
      this.ctaUpgradeParticipantInfo = false
    },
    openUpgradeBackground() {
      if (!this.isPlanFree) {
        return
      }
      this.ctaUpgradeParticipantInfo = true
    },

    handleEnablePublicSearchPresentation() {
      const payload = {
        isPublicSearch: this.presentation.isPublicSearch,
      }
      this.setSyncLoadingAction(1)
      this._updatePresentation(payload)
    },

    _updatePresentation(payload) {
      this.$http
        .put(Config.api.updatePresentation + this.$route.params.id, payload)
        .then(
          () => {
            this.setSyncLoadingAction(2)
          },
          () => {
            this.setSyncLoadingAction(3)
          },
        )
    },

    // copied from _updatePresentation method in QuizTeamConfig.vue
    async _updatePresentationFromQuizTeamConfig(payload) {
      payload.uniqueAccessCode = this.$store.state.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      this.setSyncLoadingAction(1)
      try {
        await PresentationApi.updatePresentationByPresentationId(
          this.$store.state.presentation.id,
          payload,
        )
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
        Util.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Team set up > Quiz team scoring rule or play update failed',
          JSON.stringify(error),
        )

        if (error.status) {
          throw error
        }
      }
    },

    setLang() {
      this.$store.dispatch('setLang', this.$store.state.presentation.language)
      this.setSyncLoadingAction(1)
      this.$http
        .put(
          Config.api.updatePresentation + this.$store.state.presentation.id,
          {
            language: this.$store.state.presentation.language,
            socketId: Socket.getSocketId(),
          },
        )
        .then(
          () => {
            this.setSyncLoadingAction(2)
          },
          () => {
            this.setSyncLoadingAction(3)
          },
        )
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Change Language > Language changed',
        this.$store.state.presentation.language,
      )
    },

    openModalUploadLogo() {
      this.showModalUploadLogo = true
    },
    hideModalUploadLogo() {
      this.modalUploadLogo = false
    },
    handleUploadImageForm(data) {
      this._hideModalUploadImage()
      this.imageUrlCrop = data
      this.showCropImage = true
    },
    setFontFamily() {
      this.setSyncLoadingAction(1)
      const payload = {
        fontFamily: this.$store.state.presentation.fontFamily,
        socketId: Socket.getSocketId(),
        accessCode: this.$store.state.presentation.uniqueAccessCode,
      }
      Util.setFontSlideV2(this.$store.state.presentation.fontFamily)
      this._updatePresentation(payload)
    },
    onChangeAudiencePacing() {
      const payload = {
        socketId: Socket.getSocketId(),
        audiencePacing: this.$store.state.presentation.audiencePacing,
      }
      this.setSyncLoadingAction(1)
      this._updatePresentation(payload)
      let name = 'presenter'
      if (this.$store.state.presentation.audiencePacing) {
        name = 'audience'
      }
      Util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Settings menu > Who takes the lead', name)
    },
    _hideInstructionBar() {
      this.$store.state.presentation.hideIntroBarDocument =
        this.$store.state.presentation.hideInstructionBar
      const payload = {
        socketId: Socket.getSocketId(),
        hideInstructionBar: this.$store.state.presentation.hideInstructionBar,
        hideIntroBarDocument: this.$store.state.presentation.hideInstructionBar,
      }
      this._updatePresentation(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Presenter show/hide instruction bar',
        payload.hideInstructionBar,
      )
    },
    togglePrivateFeedback() {
      const payload = {
        isEnablePrivateFeedback: this.presentation.isEnablePrivateFeedback,
        socketId: Socket.getSocketId(),
      }

      this.$store.dispatch('updatePresentation', payload)
      this._updatePresentation(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Other settings > Toggle private feedback',
        payload.isEnablePrivateFeedback ? 'Enable' : 'Disable',
        null, {
          presentationId: this.presentation.id,
        },
      )
    },

    async handleEnableSingleReaction(reactionName, reactionEnabled) {
      this.setSyncLoadingAction(1)
      const payload = {
        reactions: {
          ...this.presentation.reactions,
        },
      }
      payload.reactions[reactionName] = reactionEnabled
      this.patchPresentationAction(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Other settings > Enable reaction',
        `Reaction ${reactionEnabled ? 'enabled' : 'disabled'}: ${reactionName}`,
      )
      try {
        await PresentationApi.patchPresentation(this.presentation.id, payload)
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },

    _linkClickAble() {
      const slide = JSON.parse(JSON.stringify(this.$store.state.slideActive))
      this.$store.dispatch('updateSlideActive', {
        activeTab: null,
        id: null,
      })
      setTimeout(() => {
        this.$store.dispatch('setSlideActive', slide)
      }, 0)
      const payload = {
        showHyperLink: this.$store.state.presentation.showHyperLink,
      }
      this._updatePresentation(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Other settings > Presenter show/hide hyperlink',
        payload.showHyperLink,
      )
    },

    showShareMenu() {
      this.$store.state.presentation.showShare = !this.$store.state.presentation.showShare
      this.$store.dispatch('updatePresentation', this.$store.state.presentation)
      this.$store.state.presentation.showSetting = false
      this.$store.state.presentation.showExport = false
      this.$store.state.presentation.showBranding = false

      Util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Editor Header > Setting > Who takes the lead > Open Invite menu')

      if (!this.$store.state.presentation.qrcode
        || !this.$store.state.presentation.shareCode
        || !this.$store.state.presentation.uniqueAccessCode) {
        this.$http.get(Config.api.getPresentationBasic + this.$store.state.presentation.id)
          .then((res) => {
            this.$store.state.presentation.qrcode = res.body.qrcode
            this.$store.state.presentation.shareCode = res.body.shareCode
            this.$store.state.presentation.uniqueAccessCode = res.body.uniqueAccessCode
          })
      }
    },
    onClickPresenInfoTab() {
      Util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Settings menu > Presentation info')
    },
    setShowAhaSlidesCTA() {
      Util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, this.$store.state.presentation
        .showAhaSlidesCTA ? 'Settings menu > Enable sign-up links' : 'Settings menu > Disable sign-up links')
      const payload = {
        showAhaSlidesCTA: this.$store.state.presentation
          .showAhaSlidesCTA,
      }
      this.$store.dispatch('setSyncPresentation', 1)
      this._updatePresentation(payload)
    },
    openUpgrade() {
      this.ctaUpgrade = true

      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Feature upgrade',
        'Show sign-up link',
      )
    },
    clickOutsideUp() {
      this.ctaUpgrade = false
    },
    handleModalUpgradeApply() {
      this.ctaUpgrade = false
      this.$router.push('/apps/user-plan?redirect=/apps/user-plan')
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay-disable-setting {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full;
}

.settings-reaction__checkbox {
  @apply flex flex-col-reverse mr-4;
  &::v-deep .checkbox-component-wrapper {
    @apply mr-0;
  }
  .settings-reaction__image {
    @apply mb-2;
    width: 25px;
    height: 25px;
  }
}

.settings-accordion {
  & ::v-deep {
    .accordion-item--expanded {
      .settings-accordion__badge {
        display: none;
      }
    }
  }
}

.cta-upgrade {
  @apply bg-secondary-100 absolute w-60 p-3 rounded z-50;
  top: -9rem;
  left: calc(50% - 120px);
  font-family: 'Nunito', sans-serif;
  div,
  span {
    @apply text-base-0 text-sm;
  }
  div {
    @apply font-bold;
  }
  span {
    @apply font-normal mt-1;
  }
}
.upgrade-arrow {
  width: 0;
  height: 0;
  border-width: 0 8px 8px 8px;
  border-color: transparent transparent rgba(40, 46, 62, var(--tw-bg-opacity)) transparent;
  border-style: solid;
  position: absolute;
  z-index: 1;
  top: 1.6rem;

  &.upgrade-arrow_presentation {
    left: calc(50% - 47px);
  }

  &.upgrade-arrow_slide {
    left: 35%;
  }
}

</style>
